import React, { useMemo } from 'react';
import { Box, Link, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Translate } from 'react-localize-redux';
import { EnhancedTable } from '../../../components/general/EnhancedTable';
import LoadingState from './common/LoadingState';
import ErrorStateView from './common/ErrorState';
import { buildVisitWithoutImagesLink } from './buildVisitWithoutImagesLink';

// SKIN-35 / SKIN-55 — one row per Visit with no lesion image. Mirrors the backend
// VisitWithoutImagesResponse row shape (opaque identifiers only).
export interface PatientsWithoutImagesRow {
    patientInvestigationUuid: string;
    patientIdInvestigation: number;
    visitUuid: string;
    visitStatus: string;
    visitCreatedAt: string;
    firstSubmissionId?: number | null;
}

interface PatientsWithoutImagesProps {
    loading: boolean;
    error?: Error | null;
    rows: PatientsWithoutImagesRow[];
    onRetry?: () => void;
}

const formatDate = (iso: string): string => {
    if (!iso) return '-';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '-' : d.toLocaleDateString();
};

const PatientsWithoutImages: React.FC<PatientsWithoutImagesProps> = ({ loading, error, rows, onRetry }) => {
    const headCells = useMemo(() => ([
        { id: 'patient', alignment: 'left', label: <Translate id="hospital.analytics.clinical.patients-without-images.patient" /> },
        { id: 'visitDate', alignment: 'left', label: <Translate id="hospital.analytics.clinical.patients-without-images.visit-date" /> },
        { id: 'visitStatus', alignment: 'left', label: <Translate id="hospital.analytics.clinical.patients-without-images.visit-status" /> },
        { id: 'action', alignment: 'right', label: <Translate id="general.actions" /> },
    ]), []);

    const tableRows = useMemo(() => rows.map(r => ({
        id: r.visitUuid,
        patient: r.patientIdInvestigation,
        visitDate: formatDate(r.visitCreatedAt),
        visitStatus: r.visitStatus,
        action: (
            <Link component={RouterLink} to={buildVisitWithoutImagesLink(r)} underline="hover">
                <Translate id="hospital.analytics.clinical.patients-without-images.view-visit" />
            </Link>
        ),
    })), [rows]);

    if (loading) return <LoadingState />;
    if (error) return <ErrorStateView error={error} onRetry={onRetry} />;

    // EnhancedTable indexes rows[0] to infer column types, so it must not be
    // rendered with an empty list — show an explicit empty state instead.
    if (!rows.length) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="textSecondary">
                    <Translate id="hospital.analytics.clinical.patients-without-images.empty" />
                </Typography>
            </Box>
        );
    }

    return <EnhancedTable noHeader noSelectable disableOrder headCells={headCells} rows={tableRows} />;
};

export default PatientsWithoutImages;
